import { useState, type SubmitEvent } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { CopyIcon, KeyIcon, PlusIcon } from '@phosphor-icons/react';

import { adminApiKeysRepository } from '@web/data/repositories';
import type { AdminApiKeyDto, AdminProviderDto } from '@web/data/repositories';
import {
  Button,
  Card,
  IconButton,
  Input,
  PageSectionHead,
  useOrgContext,
  useSnackbar,
} from '@web/ui/core';
import { twcn } from '@web/ui/core/utils';
import { currentLocale, t } from '@web/i18n';

const providersKey = (orgId: string) => ['admin', 'api-keys', 'providers', orgId] as const;
const keysKey = (orgId: string, providerId: string) =>
  ['admin', 'api-keys', 'keys', orgId, providerId] as const;

const providerKeysQuery = (orgId: string, provider: AdminProviderDto) => ({
  queryKey: keysKey(orgId, provider.id),
  queryFn: () => adminApiKeysRepository.listKeys(orgId, provider.id),
});

// Slug for the provider name. Mirrors the regex in the design handoff
// (`a-z`, `0-9` and hyphen; must start with a letter or digit; 2–31 chars).
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,30}$/;

interface RevealState {
  providerId: string;
  plaintext: string;
}

function showApiError(
  showSnackbar: ReturnType<typeof useSnackbar>['showSnackbar'],
  err: unknown,
  fallback: Parameters<typeof t>[0],
): void {
  showSnackbar({ severity: 'error', message: err instanceof Error ? err.message : t(fallback) });
}

function useActiveKeyCount(providers: AdminProviderDto[], orgId: string): number {
  const keyQueries = useQueries({
    queries: providers.map((provider) => providerKeysQuery(orgId, provider)),
  });
  return keyQueries.reduce(
    (count, query) => count + (query.data?.filter((key) => !key.revokedAt).length ?? 0),
    0,
  );
}

export function ApiKeysPage(): React.JSX.Element {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const [name, setName] = useState('');
  const [reveal, setReveal] = useState<RevealState | null>(null);

  const providersQuery = useQuery({
    queryKey: providersKey(orgId),
    queryFn: () => adminApiKeysRepository.listProviders(orgId),
  });

  const createProvider = useMutation({
    mutationFn: (slug: string) => adminApiKeysRepository.createProvider(orgId, slug),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: providersKey(orgId) });
      setName('');
      showSnackbar({
        severity: 'success',
        message: t('apiKeys.toast.providerCreated', { name: created.name }),
      });
    },
    onError: (err) => {
      showApiError(showSnackbar, err, 'apiKeys.toast.createProviderError');
    },
  });

  const generateKey = useMutation({
    mutationFn: (providerId: string) =>
      adminApiKeysRepository.generateKey(orgId, providerId, 'default'),
    onSuccess: async (created, providerId) => {
      await queryClient.invalidateQueries({ queryKey: keysKey(orgId, providerId) });
      setReveal({ providerId: created.providerId, plaintext: created.plaintext });
    },
    onError: (err) => {
      showApiError(showSnackbar, err, 'apiKeys.toast.generateKeyError');
    },
  });

  const revokeKey = useMutation({
    mutationFn: (vars: { providerId: string; keyId: string }) =>
      adminApiKeysRepository.revokeKey(orgId, vars.providerId, vars.keyId),
    onSuccess: async (_data, { providerId }) => {
      await queryClient.invalidateQueries({ queryKey: keysKey(orgId, providerId) });
      showSnackbar({ severity: 'success', message: t('apiKeys.toast.keyRevoked') });
    },
    onError: (err) => {
      showApiError(showSnackbar, err, 'apiKeys.toast.revokeKeyError');
    },
  });

  const deleteProvider = useMutation({
    mutationFn: (providerId: string) => adminApiKeysRepository.deleteProvider(orgId, providerId),
    onSuccess: async ({ revokedKeyCount }) => {
      await queryClient.invalidateQueries({ queryKey: providersKey(orgId) });
      showSnackbar({
        severity: 'success',
        message:
          revokedKeyCount > 0
            ? t('apiKeys.toast.providerDeleted', { count: revokedKeyCount })
            : t('apiKeys.toast.providerDeletedEmpty'),
      });
    },
    onError: (err) => {
      showApiError(showSnackbar, err, 'apiKeys.toast.deleteProviderError');
    },
  });

  const trimmed = name.trim();
  const nameTouched = name.length > 0;
  const nameValid = SLUG_RE.test(trimmed);
  const canCreate = nameValid && !createProvider.isPending;

  const providers = providersQuery.data ?? [];
  const totalActiveKeys = useActiveKeyCount(providers, orgId);

  function submit(e: SubmitEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (canCreate) createProvider.mutate(trimmed);
  }

  let providersContent: React.ReactNode;
  if (providersQuery.isLoading) {
    providersContent = <EmptyState title={t('apiKeys.empty.loadingProviders')} />;
  } else if (providersQuery.isError) {
    providersContent = (
      <EmptyState
        title={t('apiKeys.empty.loadProvidersError')}
        sub={
          providersQuery.error instanceof Error
            ? providersQuery.error.message
            : t('apiKeys.empty.loadProvidersRetry')
        }
      />
    );
  } else if (providers.length === 0) {
    providersContent = (
      <EmptyState
        title={t('apiKeys.empty.noProviders.title')}
        sub={t('apiKeys.empty.noProviders.sub')}
      />
    );
  } else {
    providersContent = providers.map((provider) => (
      <ProviderRow
        key={provider.id}
        provider={provider}
        orgId={orgId}
        reveal={reveal}
        onGenerate={() => {
          generateKey.mutate(provider.id);
        }}
        isGenerating={generateKey.isPending && generateKey.variables === provider.id}
        onCloseReveal={() => {
          setReveal(null);
        }}
        onCopy={(value) => {
          void (async () => {
            try {
              await navigator.clipboard.writeText(value);
              showSnackbar({ severity: 'success', message: t('apiKeys.toast.copied') });
            } catch {
              showSnackbar({ severity: 'error', message: t('apiKeys.toast.copyError') });
            }
          })();
        }}
        onRevokeKey={(keyId) => {
          revokeKey.mutate({ providerId: provider.id, keyId });
        }}
        onDeleteProvider={() => {
          deleteProvider.mutate(provider.id);
        }}
        isDeletingProvider={deleteProvider.isPending && deleteProvider.variables === provider.id}
      />
    ));
  }

  return (
    <>
      <ApiKeysHeader />

      <NewProviderCard
        name={name}
        nameTouched={nameTouched}
        nameValid={nameValid}
        canCreate={canCreate}
        onNameChange={setName}
        onSubmit={submit}
      />

      <ProvidersCard
        providerCount={providers.length}
        totalActiveKeys={totalActiveKeys}
        content={providersContent}
      />
    </>
  );
}

function ApiKeysHeader(): React.JSX.Element {
  return (
    <PageSectionHead
      title={t('apiKeys.page.title')}
      sub={
        <>
          {t('apiKeys.page.subBeforeBrand')}
          <strong className="font-medium text-foreground">voll-intelligence</strong>
          {t('apiKeys.page.subAfterBrand')}
          <InlineCode>agentExternalRefId</InlineCode>
          {t('apiKeys.page.subAfterRef')}
        </>
      }
    />
  );
}

function ProvidersCard({
  providerCount,
  totalActiveKeys,
  content,
}: Readonly<{
  providerCount: number;
  totalActiveKeys: number;
  content: React.ReactNode;
}>): React.JSX.Element {
  return (
    <Card>
      <header className="flex items-center justify-between border-b border-border-soft px-5 py-3.5">
        <h3 className="m-0 text-base font-medium text-foreground">
          {t('apiKeys.provider.listTitle')}
        </h3>
        <span className="font-mono text-tiny uppercase tracking-eyebrow-loose text-muted-foreground">
          {t('apiKeys.provider.count', { count: providerCount })}
          {totalActiveKeys > 0 ? t('apiKeys.provider.activeKeys', { count: totalActiveKeys }) : ''}
        </span>
      </header>
      {content}
    </Card>
  );
}

function NewProviderCard({
  name,
  nameTouched,
  nameValid,
  canCreate,
  onNameChange,
  onSubmit,
}: Readonly<{
  name: string;
  nameTouched: boolean;
  nameValid: boolean;
  canCreate: boolean;
  onNameChange: (value: string) => void;
  onSubmit: (event: SubmitEvent<HTMLFormElement>) => void;
}>): React.JSX.Element {
  return (
    <div className="mb-4">
      <Card>
        <div className="p-5">
          <header className="mb-3.5 border-b border-border-soft pb-3">
            <h3 className="m-0 text-base font-medium text-foreground">
              {t('apiKeys.form.newProvider')}
            </h3>
            <p className="m-0 mt-1 text-meta leading-normal text-muted-foreground">
              {t('apiKeys.form.slugHelp.beforeExamples')}
              <InlineCode>n8n-prod</InlineCode>, <InlineCode>zapier-staging</InlineCode>,{' '}
              <InlineCode>make-internal</InlineCode>
              {t('apiKeys.form.slugHelp.afterExamples')}
            </p>
          </header>
          <form onSubmit={onSubmit}>
            <div className="flex items-end gap-2.5">
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <label
                  htmlFor="provider-name"
                  className="flex items-center gap-2 text-meta font-medium text-foreground"
                >
                  {t('apiKeys.form.name.label')}
                  <span className="font-mono text-tiny uppercase tracking-eyebrow-wide text-muted-foreground">
                    {t('apiKeys.form.name.required')}
                  </span>
                </label>
                <Input
                  id="provider-name"
                  value={name}
                  onChange={(event) => {
                    onNameChange(event.target.value);
                  }}
                  placeholder="n8n-prod"
                  aria-invalid={nameTouched && !nameValid}
                />
              </div>
              <Button variant="primary" size="default" type="submit" disabled={!canCreate}>
                <PlusIcon size={14} weight="regular" />
                {t('apiKeys.form.createProvider')}
              </Button>
            </div>
            {nameTouched && !nameValid ? (
              <p className="m-0 mt-1.5 text-meta text-tone-red-fg">
                {t('apiKeys.form.name.errorFormat')}
                <InlineCode>a-z</InlineCode>
                {t('apiKeys.form.name.errorFormatMiddle')}
                <InlineCode>0-9</InlineCode>
                {t('apiKeys.form.name.errorFormatEnd')}
              </p>
            ) : null}
          </form>
        </div>
      </Card>
    </div>
  );
}

interface ProviderRowProps {
  provider: AdminProviderDto;
  orgId: string;
  reveal: RevealState | null;
  onGenerate: () => void;
  isGenerating: boolean;
  onCloseReveal: () => void;
  onCopy: (value: string) => void;
  onRevokeKey: (keyId: string) => void;
  onDeleteProvider: () => void;
  isDeletingProvider: boolean;
}

function ProviderRow({
  provider,
  orgId,
  reveal,
  onGenerate,
  isGenerating,
  onCloseReveal,
  onCopy,
  onRevokeKey,
  onDeleteProvider,
  isDeletingProvider,
}: Readonly<ProviderRowProps>): React.JSX.Element {
  const keysQuery = useQuery(providerKeysQuery(orgId, provider));

  const keys = keysQuery.data ?? [];
  const activeKeyCount = keys.filter((k) => !k.revokedAt).length;
  const revealOpen = reveal?.providerId === provider.id;

  function confirmDeleteProvider(): void {
    const message =
      activeKeyCount > 0
        ? t('apiKeys.confirm.deleteWithKeys', { name: provider.name, count: activeKeyCount })
        : t('apiKeys.confirm.deleteEmpty', { name: provider.name });
    if (globalThis.confirm(message)) onDeleteProvider();
  }

  function confirmRevokeKey(keyId: string, prefix: string): void {
    if (globalThis.confirm(t('apiKeys.confirm.revokeKey', { prefix }))) onRevokeKey(keyId);
  }

  let keysContent: React.ReactNode;
  if (keysQuery.isError) {
    keysContent = (
      <div className="rounded-md border border-dashed border-tone-red-fg px-5 py-4 text-center">
        <span className="font-mono text-tiny tracking-eyebrow-narrow text-tone-red-fg">
          {t('apiKeys.keys.loadError')}
        </span>
      </div>
    );
  } else if (keys.length > 0) {
    keysContent = (
      <ul className="m-0 flex list-none flex-col rounded-md border border-border-soft bg-paper p-0">
        {keys.map((k, idx) => (
          <KeyRow
            key={k.id}
            apiKey={k}
            isFirst={idx === 0}
            onRevoke={() => {
              confirmRevokeKey(k.id, k.prefix);
            }}
          />
        ))}
      </ul>
    );
  } else {
    keysContent = (
      <div className="rounded-md border border-dashed border-border-soft px-5 py-4 text-center">
        <span className="font-mono text-tiny tracking-eyebrow-narrow text-muted-foreground">
          {t('apiKeys.keys.noKeysYet')}{' '}
          <strong className="font-medium text-foreground">{t('apiKeys.keys.generate')}</strong>{' '}
          {t('apiKeys.keys.noKeysYetSuffix')}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5 border-b border-border-soft px-5 pb-4 pt-3.5 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-card-meta font-medium text-foreground">{provider.name}</span>
          <span className="inline-flex min-w-0 items-center gap-2 font-mono text-micro text-muted-foreground">
            <span className="flex-shrink-0 text-mono-xs uppercase tracking-eyebrow-wide text-faint-foreground">
              agentExternalRefId
            </span>
            <span className="truncate">{provider.agentExternalRefId}</span>
            <IconButton
              variant="ghost"
              size="xs"
              icon="copy"
              aria-label={t('apiKeys.copyRefId')}
              title={t('apiKeys.copyRefId')}
              onClick={() => {
                onCopy(provider.agentExternalRefId);
              }}
            />
          </span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={onGenerate} disabled={isGenerating}>
            <KeyIcon size={14} weight="regular" />
            {isGenerating ? t('apiKeys.keys.generating') : t('apiKeys.keys.generate')}
          </Button>
          <IconButton
            variant="ghost"
            size="sm"
            icon="trash"
            aria-label={t('apiKeys.deleteProvider')}
            title={t('apiKeys.deleteProvider')}
            onClick={confirmDeleteProvider}
            disabled={isDeletingProvider}
          />
        </div>
      </div>

      {revealOpen ? (
        <RevealBanner
          plaintext={reveal.plaintext}
          onCopy={() => {
            onCopy(reveal.plaintext);
          }}
          onClose={onCloseReveal}
        />
      ) : null}

      {keysContent}
    </div>
  );
}

interface KeyRowProps {
  apiKey: AdminApiKeyDto;
  isFirst: boolean;
  onRevoke: () => void;
}

function KeyRow({ apiKey, isFirst, onRevoke }: Readonly<KeyRowProps>): React.JSX.Element {
  const revoked = !!apiKey.revokedAt;
  return (
    <li
      className={twcn(
        'grid grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-3 px-3 py-2',
        !isFirst && 'border-t border-border-soft',
      )}
    >
      <span
        className={twcn(
          'truncate font-mono text-card-id text-foreground',
          revoked && 'text-muted-foreground line-through',
        )}
      >
        {apiKey.prefix}
        <span className="text-faint-foreground">{'••••••••••••••'}</span>
      </span>
      {revoked ? (
        <span className="inline-flex h-5 items-center rounded-sm bg-tone-gray-tint px-1.5 text-card-id font-medium text-tone-gray-ink">
          {t('apiKeys.status.revoked')}
        </span>
      ) : (
        <span className="inline-flex h-5 items-center gap-1.5 rounded-sm bg-tone-green-tint px-1.5 text-card-id font-medium text-tone-green-ink">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-tone-green-fg" />
          {t('apiKeys.status.active')}
        </span>
      )}
      <span className="whitespace-nowrap font-mono text-tiny tracking-eyebrow-narrow text-muted-foreground">
        {t('apiKeys.key.created')} {formatDate(apiKey.createdAt)}
        <span className="mx-1 text-faint-foreground">·</span>
        {t('apiKeys.key.lastUsed')}{' '}
        {apiKey.lastUsedAt ? formatDate(apiKey.lastUsedAt) : t('apiKeys.key.never')}
      </span>
      {revoked ? (
        // Grid expects 4 columns; keep the cell so the layout doesn't reflow
        // when revoked keys hide the revoke button.
        <span aria-hidden />
      ) : (
        <IconButton
          variant="ghost"
          size="xs"
          icon="prohibit"
          aria-label={t('apiKeys.revoke')}
          title={t('apiKeys.revoke')}
          onClick={onRevoke}
        />
      )}
    </li>
  );
}

interface RevealBannerProps {
  plaintext: string;
  onCopy: () => void;
  onClose: () => void;
}

function RevealBanner({
  plaintext,
  onCopy,
  onClose,
}: Readonly<RevealBannerProps>): React.JSX.Element {
  return (
    <div
      role="alert"
      className="flex items-center gap-3 rounded-md border border-primary/20 bg-primary-tint px-3 py-2.5"
    >
      <KeyIcon size={16} weight="regular" className="flex-shrink-0 text-primary" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="font-mono text-mono-xs uppercase tracking-eyebrow-wider text-primary-ink">
          {t('apiKeys.reveal.title')}
        </span>
        <span className="select-all truncate font-mono text-base text-foreground">{plaintext}</span>
      </div>
      <Button variant="outline" size="sm" onClick={onCopy}>
        <CopyIcon size={14} weight="regular" />
        {t('apiKeys.copy')}
      </Button>
      <IconButton
        variant="ghost"
        size="sm"
        icon="close"
        aria-label={t('apiKeys.close')}
        onClick={onClose}
      />
    </div>
  );
}

function EmptyState({ title, sub }: Readonly<{ title: string; sub?: string }>): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
      <KeyIcon size={22} weight="regular" className="text-faint-foreground" />
      <span className="text-card-meta font-medium text-foreground">{title}</span>
      {sub ? <span className="text-meta text-muted-foreground">{sub}</span> : null}
    </div>
  );
}

function InlineCode({ children }: Readonly<{ children: React.ReactNode }>): React.JSX.Element {
  return (
    <code className="rounded-sm bg-muted px-1.5 py-px font-mono text-micro text-foreground">
      {children}
    </code>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(currentLocale(), { day: '2-digit', month: 'short' });
}
